import { type FC, useMemo } from "react";
import { Button, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "@/shared/components/Form";
import { DialogHeader } from "@/shared/components";
import {
  makePlatformPageFormSchema,
  type PlatformPageFormValues,
} from "../model";

/** Состояние страницы, от которого зависят подписи кнопок формы. */
type PageFormState = "draft" | "published";

const PRIMARY_ACTION_LABEL_KEYS: Record<PageFormState, string> = {
  draft: "common.actions.publish",
  published: "common.actions.save",
};

const SECONDARY_ACTION_LABEL_KEYS: Record<PageFormState, string> = {
  draft: "common.actions.save",
  published: "common.actions.hide",
};

interface PageFormProps {
  title: string;
  loading?: boolean;
  initialValues?: PlatformPageFormValues;
  onSubmit: (values: PlatformPageFormValues) => Promise<void>;
  onClose: () => void;
}

export const PageForm: FC<PageFormProps> = ({
  title,
  loading = false,
  initialValues = {
    title: "",
    path: "",
    description: null,
    imageId: null,
    publishAt: null,
    isHomepage: false,
    countryCodes: [],
    visibilityConditions: {
      userType: "ALL",
      countries: {
        type: "ALL",
        countryCodes: [],
      },
      applicationPlatforms: {
        type: "ALL",
        platforms: [],
      },
    },
    blocks: [],
  },
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const schema = useMemo(() => makePlatformPageFormSchema(t), [t]);

  const isDraft = !initialValues.publishAt;
  const state: PageFormState = isDraft ? "draft" : "published";
  let publish = !isDraft;

  const handleSubmit = async (values: Record<string, any>) => {
    await onSubmit({
      ...(values as PlatformPageFormValues),
      publishAt: publish ? new Date().toISOString() : null,
    });
  };

  return (
    <>
      <DialogHeader
        title={title}
        actions={[
          <Button
            variant="contained"
            type="submit"
            form="platform-page-form"
            disabled={loading}
            onClick={() => (publish = true)}
          >
            {t(PRIMARY_ACTION_LABEL_KEYS[state])}
          </Button>,
          <Button
            variant="outlined"
            type="submit"
            form="platform-page-form"
            disabled={loading}
            onClick={() => (publish = false)}
          >
            {t(SECONDARY_ACTION_LABEL_KEYS[state])}
          </Button>,
        ]}
        onClose={onClose}
        loading={loading}
      />

      <DialogContent sx={{ p: 1 }}>
        <FormBuilder
          formId="platform-page-form"
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          disabled={loading}
          submitButton={false}
          resetButton={false}
        />
      </DialogContent>
    </>
  );
};
